import { useCallback, useEffect, useRef, useState } from "react";
import { POLL_MS, YOUR_TURN_MS } from "../config";
import { toActivity } from "../data/activityAdapter";
import {
  findAttempt,
  finishStep,
  getOrCreateAttempt,
  saveStep,
} from "../data/learnerService";
import { DENIED, SETUP, UNAVAILABLE, UNSUPPORTED, problemOf } from "../data/problems";

// Waiting → Your turn → steps → Complete (handoff §9).
//
// phase: "checking" | "waiting" | "your-turn" | "activity" | "complete"
//      | "signed-out" | "not-ready"
// "signed-out" means this tablet has not been set up (or was reset).
// `reason` explains "not-ready" to the adult: "not-enrolled", "no-access"
// (this tablet may not read the learner's class or work), "release-unavailable"
// (the release state itself could not be read, which is a setup problem rather
// than an account problem) or "unreadable" (the activity or a record did not
// match the expected shape) or "unsupported-device" (the tablet's browser is
// too old for this activity).
// While a save is in flight, `saving` is true; if it fails, `retry` holds the
// action to try again and the screen stays where it is.

// Directus answers "forbidden" both when an account may not read something and
// when what is being read does not exist, so a refused read is explained by
// which read it was rather than by the error alone.
const from = (source, work) =>
  work.catch((error) => {
    if (error && typeof error === "object" && !error.source) error.source = source;
    throw error;
  });

const DENIED_REASON = {
  release: "release-unavailable",
};

// The attempt stores the step's position. If it is missing or no longer fits
// the activity, the saved responses say where the learner had got to.
const stepIndexOf = (activity, attempt) => {
  const saved = attempt.currentStep;
  if (Number.isInteger(saved) && saved >= 0 && saved < activity.steps.length)
    return saved;
  const unanswered = activity.steps.findIndex((s) => !attempt.responses[s.id]);
  return unanswered >= 0 ? unanswered : activity.steps.length - 1;
};

const useLearnerSession = (repo) => {
  const [state, setState] = useState({ phase: "checking" });
  const [saving, setSaving] = useState(false);
  const [retry, setRetry] = useState(null);
  const entering = useRef(false);
  // Whether this tablet is set up is asked once, before its first read.
  const setUp = useRef(false);

  const settleProblem = useCallback(
    async (error, fallbackPhase) => {
      const problem = problemOf(error);
      if (problem === DENIED) {
        setState(
          (await repo.isSignedIn())
            ? {
                phase: "not-ready",
                reason: DENIED_REASON[error?.source] || "no-access",
              }
            : { phase: "signed-out" }
        );
      } else if (problem === SETUP) {
        setState({ phase: "not-ready", reason: "release-unavailable" });
      } else if (problem === UNSUPPORTED) {
        setState({ phase: "not-ready", reason: "unsupported-device" });
      } else if (problem === UNAVAILABLE) {
        setState({ phase: "not-ready", reason: "unreadable" });
      } else {
        setState((s) => (s.phase === "checking" ? { phase: fallbackPhase } : s));
      }
    },
    [repo]
  );

  // Reads where the learner is. Creates nothing.
  const resolve = useCallback(async () => {
    try {
      if (!setUp.current) {
        if (!(await repo.isSignedIn())) {
          setState({ phase: "signed-out" });
          return;
        }
        setUp.current = true;
      }
      const enrolled = await from(
        "enrolment",
        repo.isEnrolled()
      );
      if (!enrolled) {
        setState({ phase: "not-ready", reason: "not-enrolled" });
        return;
      }
      const release = await from(
        "release",
        repo.currentRelease()
      );
      if (!release) {
        setState({ phase: "waiting" });
        return;
      }
      const activity = toActivity(
        await from("activity", repo.readActivity(release.activityId))
      );
      const attempt = await from(
        "work",
        findAttempt(repo, { release })
      );
      if (attempt?.completed) setState({ phase: "complete" });
      else if (attempt) {
        setState({
          phase: "activity",
          release,
          activity,
          attempt,
          stepIndex: stepIndexOf(activity, attempt),
        });
      } else setState({ phase: "your-turn", release, activity });
    } catch (error) {
      settleProblem(error, "waiting");
    }
  }, [repo, settleProblem]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  // Simple polling while waiting for the teacher. A failed check just waits
  // for the next one.
  const phase = state.phase;
  useEffect(() => {
    if (phase !== "waiting") return undefined;
    const timer = setInterval(resolve, POLL_MS);
    return () => clearInterval(timer);
  }, [phase, resolve]);

  // Runs a save; moves on only once the server has confirmed it.
  const run = useCallback(async (action) => {
    setSaving(true);
    setRetry(null);
    try {
      await action();
    } catch {
      setRetry(() => () => run(action));
    } finally {
      setSaving(false);
    }
  }, []);

  // The attempt is created as the learner enters the first interaction, not
  // at release (handoff §11).
  const enter = useCallback(() => {
    if (entering.current || state.phase !== "your-turn") return;
    entering.current = true;
    const { release, activity } = state;
    run(async () => {
      try {
        const attempt = await getOrCreateAttempt(repo, { release });
        setState({
          phase: attempt.completed ? "complete" : "activity",
          release,
          activity,
          attempt,
          stepIndex: stepIndexOf(activity, attempt),
        });
      } finally {
        entering.current = false;
      }
    });
  }, [state, repo, run]);

  // The hand-off cue advances itself. The timer changes the UI into the first
  // interaction; attempt creation belongs to that transition and never to
  // Waiting, release discovery, or the time the cue remains visible.
  useEffect(() => {
    if (state.phase !== "your-turn") return undefined;
    const timer = setTimeout(enter, YOUR_TURN_MS);
    return () => clearTimeout(timer);
  }, [state.phase, enter]);

  // Saves the finished step's meaningful result, with its picture as evidence
  // when there is one (`blob` is null when nothing was drawn), then moves on.
  // The last step completes the attempt.
  const completeStep = useCallback(
    (response, blob = null) => {
      if (state.phase !== "activity") return;
      const { activity, attempt, stepIndex } = state;
      const step = activity.steps[stepIndex];
      const last = stepIndex === activity.steps.length - 1;
      run(async () => {
        const saved = await finishStep(repo, attempt, {
          stepId: step.id,
          stepIndex,
          response,
          blob,
          last,
        });
        setState((s) =>
          last
            ? { phase: "complete" }
            : { ...s, attempt: saved, stepIndex: stepIndex + 1 }
        );
      });
    },
    [state, repo, run]
  );

  // Keeps part of the current step (a counting estimate) so a refresh returns
  // to the same phase. The learner stays on the step.
  const saveProgress = useCallback(
    (response) => {
      if (state.phase !== "activity") return;
      const { activity, attempt, stepIndex } = state;
      run(async () => {
        const saved = await saveStep(repo, attempt, {
          stepId: activity.steps[stepIndex].id,
          response,
          nextStepIndex: stepIndex,
        });
        setState((s) => ({ ...s, attempt: saved }));
      });
    },
    [state, repo, run]
  );

  // A part the activity cannot do without (a tracing template) could not be
  // shown. The adult sees the calm "can't be shown" message; Check again reads
  // everything afresh and resumes the same attempt.
  const unavailable = useCallback(
    () => setState({ phase: "not-ready", reason: "unreadable" }),
    []
  );

  return {
    ...state,
    saving,
    retry,
    unavailable,
    recheck: resolve,
    signOut: async () => {
      await repo.signOut();
      setUp.current = false;
      setState({ phase: "signed-out" });
    },
    completeStep,
    saveProgress,
  };
};

export default useLearnerSession;
